/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { type Registry as Services, service } from '@ember/service';
import type BookingModel from 'uplisting-frontend/models/booking';

interface IArgs {
  booking: BookingModel;
  /**
   * One-row variant for tight headers (the calendar side-panel card): the guest
   * total with the full breakdown in a tooltip, plus a paw chip with the pet count.
   */
  compact?: boolean;
}

interface GuestBreakdownSignature {
  Element: HTMLElement;
  Args: IArgs;
}

const GUEST_LINE_PARTS = [
  ['numberOfAdults', 'adults'],
  ['numberOfChildren', 'children'],
  ['numberOfInfants', 'infants'],
] as const;

export default class UiBookingGuestBreakdownComponent extends Component<GuestBreakdownSignature> {
  @service intl!: Services['intl'];

  get guestLine(): string {
    return GUEST_LINE_PARTS.filter(
      ([attribute]) => (this.args.booking[attribute] ?? 0) > 0,
    )
      .map(([attribute, key]) =>
        this.intl.t(`action_bookings_summary.guest_breakdown.${key}`, {
          count: this.args.booking[attribute],
        }),
      )
      .join(' · ');
  }

  get hasPets(): boolean {
    return (this.args.booking.numberOfPets ?? 0) > 0;
  }

  get fullBreakdown(): string {
    const parts = [this.guestLine];

    if (this.hasPets) {
      parts.push(
        this.intl.t('action_bookings_summary.guest_breakdown.pets', {
          count: this.args.booking.numberOfPets,
        }),
      );
    }

    return parts.filter(Boolean).join(' · ');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::GuestBreakdown': typeof UiBookingGuestBreakdownComponent;
  }
}
